import { DateTime } from "luxon";
import { z } from "zod";

import { zJsonToString, zStringToJson } from "~/utilities/zodInputHelpers";

import { ethnicitySchema } from "../ethnicities/types";

export const addressSchema = z.object({
  id: z.number(),
  street1: z.string().optional(),
  street2: z.string().optional(),
  suburb: z.string().optional(),
  city: z.string().optional(),
  postcode: z.string().optional(),
  region: z.string().optional(),
  country: z.string().optional(),
  addressType: z.object({ id: z.number(), name: z.string() }),
  isPrimaryMailing: z.boolean().optional(),
});

export type Address = z.infer<typeof addressSchema>;

const clientGender = z.object({
  code: z.enum(["F", "M", "O", "U"]),
  detail: z.string().optional(),
});

export type ClientGender = z.infer<typeof clientGender>;

const clientBaseSchema = z.object({
  accessLevel: z.string(),
  address: z.string().optional(), // Legacy single address field
  addresses: z.array(addressSchema),
  clientGender: clientGender,
  clientId: z.string(),
  clinicId: z.number(),
  created: z.string(),
  createdBy: z
    .object({
      id: z.number(),
      name: z.string(),
    })
    .optional(),
  dateOfBirth: z.string().date().optional(),
  email: z.string().optional(),
  firstName: z.string().optional(),
  groupingKey: z.number(),
  id: z.number(),
  lastName: z.string().optional(),
  medicalServiceId: z.string().optional(),
  middleName: z.string().optional(),
  phone: z.string().optional(),
  preferredName: z.string().optional(),
  restricted: z.boolean(),
  securePatientId: z.number(),
  sid: z.string(),
});

export const clientSummarySchema = clientBaseSchema.extend({
  currentStatus: z.object({
    id: z.number(),
    active: z.boolean(),
    patientStatusReasonId: z.number().optional(),
  }),
});

export type ClientSummary = z.infer<typeof clientSummarySchema>;

const relationshipTypeSchema = z.object({
  id: z.number(),
  personTitle: z.string(),
  visible: z.boolean(),
});

const accessControlSchema = z.object({
  id: z.number(),
  condition: z.object({
    data: z.object({
      firstName: z.string(),
      lastName: z.string(),
    }),
  }),
});

export type AccessControl = z.infer<typeof accessControlSchema>;

const referralSchema = z.object({
  content: zStringToJson(),
  formId: z.number(),
  id: z.number(),
  patientId: z.number(),
});

export type Referral = z.infer<typeof referralSchema>;

const teamSummarySchema = z.object({
  id: z.number(),
  name: z.string(),
  isDefault: z.boolean(),
});

const relationshipPatientSchema = z.object({
  id: z.number(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  middleName: z.string().optional(),
  preferredName: z.string().optional(),
});

export type RelationshipPatient = z.infer<typeof relationshipPatientSchema>;

const relationshipSchema = z.object({
  id: z.number(),
  patientA: relationshipPatientSchema,
  patientB: relationshipPatientSchema,
  relationshipTypeA: relationshipTypeSchema,
  relationshipTypeB: relationshipTypeSchema,
  shortNoteA: z.string().optional(),
  shortNoteB: z.string().optional(),
});

export type Relationship = z.infer<typeof relationshipSchema>;

export const clientResponseSchema = clientBaseSchema.extend({
  accessControl: z.array(accessControlSchema).optional(),
  alternativeContactName: z.string().optional(),
  alternativeContactPhone: z.string().optional(),
  alternativeContactRelationship: z.string().optional(),
  backgroundInfo: z.string().optional(),
  clientGender: clientGender.optional(),
  dateOfDeath: z.string().date().optional(),
  email: z.string().optional(),
  ethnicities: z.array(ethnicitySchema),
  gpName: z.string().optional(),
  gpPhone: z.string().optional(),
  gpPracticeAddress: z.string().optional(),
  gpPracticeName: z.string().optional(),
  medicalChecklistContents: zStringToJson(),
  medicalChecklistId: z.number(),
  occupation: z.string().optional(),
  patientRelationshipsA: z.array(relationshipSchema),
  patientRelationshipsB: z.array(relationshipSchema),
  patientsReferrals: z.array(referralSchema),
  phone: z.string().optional(),
  secondaryPhone: z.string().optional(),
  selfUpdatedLast: z.boolean(),
  status: z.object({
    active: z.boolean(),
    created: z.string(),
    id: z.number(),
    patientStatusReasonId: z.number().optional(),
  }),
  titleType: z
    .object({
      created: z.string(),
      id: z.number(),
      name: z.string(),
      visible: z.boolean(),
    })
    .optional(),
});

export type ClientResponse = z.infer<typeof clientResponseSchema>;

const clientTeamSchema = z.object({
  id: z.number(),
  key: z.string(),
  name: z.string(),
  description: z.string().optional(),
  recordSensitivity: z.string().optional(),
  purchaseOrdersEnabled: z.boolean().optional(),
  default: z.boolean().optional(),
  modalityName: z.string().optional(),
  modalityId: z.number().optional(),
});

export type ClientTeam = z.infer<typeof clientTeamSchema>;

export const clientTeamsListSchema = z.array(clientTeamSchema);

export type ClientTeamsList = z.infer<typeof clientTeamsListSchema>;

export type SinceOption = {
  name: string;
  value: DateTime | undefined;
};

export type TeamFilter = {
  type: "in" | "not_in";
  teamIds: Array<number> | "any";
  and?: Omit<TeamFilter, "and">;
};

export type AdminClientSearchRequest = {
  page: number;
  size: number;
  teamFilter?: TeamFilter;
  nameFilter?: string | undefined;
  clientsCreatedSince: DateTime | undefined;
  allTeams: Array<number>;
};

export type TeamSummary = z.infer<typeof teamSummarySchema>;

const ClientSearchResultSchema = z.object({
  id: z.number(),
  email: z.string().optional(),
  phone: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  gender: z.string().optional(),
  medicalServiceId: z.string().optional(),
  clientId: z.string().optional(),
  isActive: z.boolean().optional(),
  dateOfBirth: z.string().optional(),
  created: z.string().optional(),
  createdBy: z
    .object({
      id: z.number(),
      name: z.string(),
    })
    .optional(),
  teams: z.array(teamSummarySchema).optional(),
  accessLevel: z.string().optional(),
  securePatientId: z.number().optional(),
  restricted: z.boolean().optional(),
});
export type ClientSearchResult = z.infer<typeof ClientSearchResultSchema>;

export const AdminClientSearchResponseSchema = z.object({
  page: z.number(),
  size: z.number(),
  sortdir: z.string().optional(),
  sortattr: z.string().optional(),
  results: z.array(ClientSearchResultSchema),
  pages: z.number(),
  count: z.number(),
  total: z.number(),
});

export type AdminClientSearchResponse = z.infer<typeof AdminClientSearchResponseSchema>;

export const createClientRequestSchema = z.object({
  titleType: z
    .object({
      id: z.number(),
    })
    .optional(),
  firstName: z.string().optional(),
  preferredName: z.string().optional(),
  middleName: z.string().optional(),
  lastName: z.string(),
  dateOfBirth: z.string().date().optional(),
  dateOfDeath: z.string().date().optional(),
  occupation: z.string().optional(),
  medicalServiceId: z.string().optional(),
  backgroundInfo: z.string().optional(),
  clientGender: z
    .object({
      code: z.string(),
      detail: z.string().optional(),
    })
    .optional(),
});

export type CreateClientRequest = z.infer<typeof createClientRequestSchema>;

export const updateClientRequestSchema = createClientRequestSchema.extend({
  id: z.number(),
  dateOfBirth: z.string().optional(),
  dateOfDeath: z.string().optional(),
  medicalChecklistId: z.number().optional(),
  medicalChecklistContents: zJsonToString().optional(),
  sid: z.string(),
});

export type UpdateClientRequest = z.infer<typeof updateClientRequestSchema>;
